import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Check, X, Loader2, AlertCircle, Wand2 } from 'lucide-react';
import {
  ConversationStyleInput,
  Voice,
  isFishVoiceId,
  voiceMatchesSelfHostedTts,
} from '../../types/campaign';
import Button from '../UI/Button';
import Switch from '../UI/Switch';
import { conversationStyleService } from '@/services/conversationStyleService';
import { campaignService } from '@/services/campaignService';
import { ConversationStyleForm } from './ConversationStyleForm';

export type BotEngine =
  | 'telnyx_assistant'
  | 'conversation_relay'
  | 'vapi_direct'
  | 'vapi_bridge'
  | 'self_hosted';

interface ConversationStylesManagerProps {
  conversationStyles: ConversationStyleInput[];
  onChange: (styles: ConversationStyleInput[]) => void;
  campaignId?: string;
  onGeneratingChange?: (isGenerating: boolean) => void;
  /** Controls which voice catalog to load. Defaults to telnyx when omitted. */
  botEngine?: BotEngine;
}

interface EditingStyle extends ConversationStyleInput {
  tempId: string;
  enhancedPromptScript?: string;
  selectedVoiceId?: string;
  voiceConfig?: any;
  transcriber?: 'deepgram' | '11labs' | 'assembly-ai' | 'azure' | 'gladia' | 'google' | 'openai';
}

const styleLabels = {
  natural: 'Natural',
  professional: 'Professional',
  casual: 'Casual'
} as const;

const personalityLabels = {
  curious: 'Curious',
  helpful: 'Helpful',
  friendly: 'Friendly'
} as const;

const approachLabels = {
  gentle: 'Gentle',
  consultative: 'Consultative',
  direct: 'Direct'
} as const;

function isVapiBotEngine(engine?: BotEngine): boolean {
  return engine === 'vapi_direct' || engine === 'vapi_bridge';
}

/**
 * Engines whose TTS runs on our side against ElevenLabs by default, so the
 * campaign's `selectedVoiceId` has to be an ElevenLabs id — a Telnyx NaturalHD
 * id is silently dropped by `toElevenLabsVoiceId` and the call falls back to the
 * server default voice. Self-hosted can also pick Fish Audio (`fish.<id>`).
 */
function usesElevenLabsCatalog(engine?: BotEngine): boolean {
  return engine === 'conversation_relay' || engine === 'self_hosted';
}

/** Same curated set as backend `/providers/telnyx/voices` — Telnyx has no dynamic voice API. */
const TELNYX_CURATED_VOICES: Voice[] = [
  {
    id: 'telnyx-naturalhd-luna',
    name: 'Luna',
    provider: 'telnyx',
    providerId: 'Telnyx.NaturalHD.luna',
    language: 'en-US',
    gender: 'female',
  },
  {
    id: 'telnyx-naturalhd-astra',
    name: 'Astra',
    provider: 'telnyx',
    providerId: 'Telnyx.NaturalHD.astra',
    language: 'en-US',
    gender: 'female',
  },
  {
    id: 'telnyx-naturalhd-albion',
    name: 'Albion',
    provider: 'telnyx',
    providerId: 'Telnyx.NaturalHD.albion',
    language: 'en-US',
    gender: 'male',
  },
  {
    id: 'telnyx-naturalhd-amarante',
    name: 'Amarante',
    provider: 'telnyx',
    providerId: 'Telnyx.NaturalHD.amarante',
    language: 'en-US',
    gender: 'female',
  },
];

/** Fallback when backend is unreachable — same names as backend catalog. */
const ELEVENLABS_CURATED_VOICES: Voice[] = [
  { id: 'elevenlabs-Fz9DTenaCNLwUrd0tLaf', name: 'Dean - Business Owner', provider: 'elevenlabs', providerId: 'Fz9DTenaCNLwUrd0tLaf', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-hLygPNd2gK6Azddorc5W', name: 'Alex - Conversational Business', provider: 'elevenlabs', providerId: 'hLygPNd2gK6Azddorc5W', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-lUTamkMw7gOzZbFIwmq4', name: 'James - Professional British Male', provider: 'elevenlabs', providerId: 'lUTamkMw7gOzZbFIwmq4', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-QAg5vr34VHswVNMk2mjq', name: 'Aiden', provider: 'elevenlabs', providerId: 'QAg5vr34VHswVNMk2mjq', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-q9WlCDSsgl3HR1iEv6iq', name: 'Kayse - Sales & Customer services', provider: 'elevenlabs', providerId: 'q9WlCDSsgl3HR1iEv6iq', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-P0SJWrIqnGXE2qxjsVX5', name: 'Josh Wetherall - Happy', provider: 'elevenlabs', providerId: 'P0SJWrIqnGXE2qxjsVX5', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-1Y2BkLYIcgqiTXiXB425', name: 'Alan - British, Engaging, Persuasive', provider: 'elevenlabs', providerId: '1Y2BkLYIcgqiTXiXB425', language: 'en-US', gender: 'male' },
  { id: 'elevenlabs-NbkKnEAZ7Bqw4EAkVEaz', name: 'Olivia J - Confident & Engaging Voice', provider: 'elevenlabs', providerId: 'NbkKnEAZ7Bqw4EAkVEaz', language: 'en-US', gender: 'female' },
  { id: 'elevenlabs-7GEvpCKf5Nlxqwoji96A', name: 'Mike - The Sales King', provider: 'elevenlabs', providerId: '7GEvpCKf5Nlxqwoji96A', language: 'en-US', gender: 'male' },
];

function voiceCatalogHint(botEngine?: BotEngine, ttsProvider?: 'elevenlabs' | 'fish'): string {
  if (isVapiBotEngine(botEngine)) {
    return 'Curated ElevenLabs voices (9). Link ElevenLabs in Vapi for display names.';
  }
  if (botEngine === 'self_hosted' && ttsProvider === 'fish') {
    return 'Fish Audio licensed voices (female/sales + your library). Self-hosted only.';
  }
  if (usesElevenLabsCatalog(botEngine)) {
    return botEngine === 'self_hosted'
      ? 'ElevenLabs voices for self-hosted TTS. Use the TTS provider switch below for Fish Audio.'
      : 'Curated ElevenLabs voices (9) via Telnyx Custom AI.';
  }
  return 'Fixed Telnyx NaturalHD voices (Luna, Astra, Albion, Amarante) — no extra catalog.';
}

function styleVoiceLabel(selectedVoiceId: string | undefined, voices: Voice[]): string {
  const id = (selectedVoiceId || '').trim();
  if (!id) return '—';
  const match = voices.find((v) => v.providerId === id);
  if (match?.name) {
    return isFishVoiceId(id) ? `${match.name} · Fish Audio` : match.name;
  }
  if (isFishVoiceId(id)) return 'Fish Audio';
  return id;
}

function voiceFieldLabel(botEngine?: BotEngine, ttsProvider?: 'elevenlabs' | 'fish'): string {
  if (botEngine === 'self_hosted' && ttsProvider === 'fish') {
    return 'Fish Audio voice';
  }
  if (isVapiBotEngine(botEngine) || usesElevenLabsCatalog(botEngine)) {
    return 'ElevenLabs voice';
  }
  return 'Telnyx voice (NaturalHD)';
}

export const ConversationStylesManager: React.FC<ConversationStylesManagerProps> = ({
  conversationStyles,
  onChange,
  campaignId,
  onGeneratingChange,
  botEngine = 'telnyx_assistant',
}) => {
  const [generatingPrompt, setGeneratingPrompt] = useState<string | null>(null);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [selfHostedTts, setSelfHostedTts] = useState<'elevenlabs' | 'fish'>('elevenlabs');

  console.log('ConversationStylesManager received styles:', conversationStyles);

  useEffect(() => {
    if (botEngine !== 'self_hosted') {
      setSelfHostedTts('elevenlabs');
      return;
    }
    const hasFish = conversationStyles.some((style) => isFishVoiceId(style.selectedVoiceId));
    setSelfHostedTts(hasFish ? 'fish' : 'elevenlabs');
    // Only when the engine changes — do not fight the user's TTS toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botEngine]);

  const [editingStyle, setEditingStyle] = useState<EditingStyle | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchVoices = async () => {
      setLoadingVoices(true);
      setVoiceError(null);
      const useVapi = isVapiBotEngine(botEngine);
      const useFish = botEngine === 'self_hosted' && selfHostedTts === 'fish';
      const useCustomAi = usesElevenLabsCatalog(botEngine) && !useFish;

      if (useFish) {
        try {
          const { voices: list, warning } = await campaignService.getFishAudioVoices();
          if (cancelled) return;
          setVoices(list);
          if (warning) setVoiceError(warning);
        } catch (error) {
          console.error('Failed to fetch Fish Audio voices:', error);
          if (cancelled) return;
          setVoices([]);
          setVoiceError('Could not load Fish Audio voices. Confirm FISH_API_KEY on the backend.');
        } finally {
          if (!cancelled) setLoadingVoices(false);
        }
        return;
      }

      if (useCustomAi) {
        try {
          const { voices: list, warning } = await campaignService.getCustomAiElevenLabsVoices();
          if (cancelled) return;
          setVoices(list.length > 0 ? list : ELEVENLABS_CURATED_VOICES);
          if (warning) {
            setVoiceError(warning);
          }
        } catch (error) {
          console.error('Failed to fetch Custom AI ElevenLabs voices:', error);
          if (cancelled) return;
          setVoices(ELEVENLABS_CURATED_VOICES);
          setVoiceError('Could not load ElevenLabs voice names from Telnyx; showing numbered voice labels.');
        } finally {
          if (!cancelled) setLoadingVoices(false);
        }
        return;
      }

      if (!useVapi) {
        // Telnyx: curated only (no dynamic voice library from Telnyx)
        try {
          const list = await campaignService.getTelnyxVoices();
          if (cancelled) return;
          setVoices(list.length > 0 ? list : TELNYX_CURATED_VOICES);
        } catch {
          if (cancelled) return;
          setVoices(TELNYX_CURATED_VOICES);
        } finally {
          if (!cancelled) setLoadingVoices(false);
        }
        return;
      }

      // Vapi: ElevenLabs voices only (curated on backend)
      try {
        const list = await campaignService.getVoicesByProvider('11labs');
        if (cancelled) return;
        setVoices(list.length > 0 ? list : ELEVENLABS_CURATED_VOICES);
        if (list.length === 0) {
          setVoiceError(
            'No ElevenLabs voices from Vapi; showing numbered labels. Add ELEVENLABS_API_KEY on backend for real names.'
          );
        }
      } catch (error) {
        console.error('Failed to fetch ElevenLabs voices:', error);
        if (cancelled) return;
        setVoices(ELEVENLABS_CURATED_VOICES);
        setVoiceError(
          'Could not load ElevenLabs voices from Vapi; showing numbered voice labels.'
        );
      } finally {
        if (!cancelled) setLoadingVoices(false);
      }
    };

    fetchVoices();
    return () => {
      cancelled = true;
    };
  }, [botEngine, selfHostedTts]);

  const handleGeneratePrompt = async (style: EditingStyle) => {
    setGeneratingPrompt(style.tempId);
    onGeneratingChange?.(true);
    try {
      const result = await conversationStyleService.generatePrompt({
          campaignId: campaignId || undefined,
          conversationStyle: style.conversationStyle,
          personalityType: style.personalityType,
          persuasionApproach: style.persuasionApproach,
        });
      
      setEditingStyle({
          ...style,
          enhancedPromptScript: result.prompt,
        });
    } catch (error) {
      console.error('Failed to generate prompt:', error);
      alert('Failed to generate prompt. Please try again.');
    } finally {
      setGeneratingPrompt(null);
      onGeneratingChange?.(false);
    }
  };

  const handleAddStyle = () => {
    const newStyle: EditingStyle = {
      tempId: Date.now().toString(),
      name: '',
      conversationStyle: 'natural',
      personalityType: 'helpful',
      persuasionApproach: 'consultative',
      transcriber: 'deepgram',
      isActive: true,
      enhancedPromptScript: '',
      selectedVoiceId: '',
      voiceConfig: {}
    };
    setEditingStyle(newStyle);
    setIsAdding(true);
  };

  const handleEditStyle = (index: number) => {
    const style = conversationStyles[index];
    setEditingStyle({
      ...style,
      tempId: index.toString(),
      id: style.id,
      enhancedPromptScript: style.enhancedPromptScript || '',
      selectedVoiceId: style.selectedVoiceId || '',
      voiceConfig: style.voiceConfig || {},
      transcriber: style.transcriber || 'deepgram'
    });
    setEditingIndex(index);
    setIsAdding(false);
    if ((style.selectedVoiceId || '').trim()) {
      setSelfHostedTts(isFishVoiceId(style.selectedVoiceId) ? 'fish' : 'elevenlabs');
    }
  };

  const handleTtsProviderChange = (next: 'elevenlabs' | 'fish') => {
    setSelfHostedTts(next);
    const nextStyles = conversationStyles.map((style) =>
      voiceMatchesSelfHostedTts(style.selectedVoiceId, next)
        ? style
        : { ...style, selectedVoiceId: '', voiceConfig: undefined }
    );
    onChange(nextStyles);
    setEditingStyle((current) => {
      if (!current) return current;
      if (voiceMatchesSelfHostedTts(current.selectedVoiceId, next)) return current;
      return { ...current, selectedVoiceId: '', voiceConfig: undefined };
    });
  };

  const handleSaveStyle = () => {
    if (!editingStyle || !editingStyle.name.trim()) return;
    if (
      botEngine === 'self_hosted' &&
      !voiceMatchesSelfHostedTts(editingStyle.selectedVoiceId, selfHostedTts)
    ) {
      return;
    }

    const styleToSave = {
      id: editingStyle.id,
      name: editingStyle.name,
      conversationStyle: editingStyle.conversationStyle,
      personalityType: editingStyle.personalityType,
      persuasionApproach: editingStyle.persuasionApproach,
      transcriber: editingStyle.transcriber,
      isActive: editingStyle.isActive,
      enhancedPromptScript: editingStyle.enhancedPromptScript,
      selectedVoiceId: editingStyle.selectedVoiceId,
      voiceConfig: editingStyle.voiceConfig,
    };

    console.log('ConversationStylesManager - Saving style:', {
      name: styleToSave.name,
      selectedVoiceId: styleToSave.selectedVoiceId,
      voiceConfig: styleToSave.voiceConfig,
      voiceConfigType: typeof styleToSave.voiceConfig,
      isVoiceConfigUndefined: styleToSave.voiceConfig === undefined,
      isVoiceConfigNull: styleToSave.voiceConfig === null,
      isVoiceConfigEmptyObject: styleToSave.voiceConfig && typeof styleToSave.voiceConfig === 'object' && Object.keys(styleToSave.voiceConfig).length === 0
    });

    if (isAdding) {
      const { id: _newId, ...created } = styleToSave;
      onChange([...conversationStyles, created]);
    } else {
      const index = parseInt(editingStyle.tempId, 10);
      const updatedStyles = [...conversationStyles];
      updatedStyles[index] = {
        ...updatedStyles[index],
        ...styleToSave,
      };
      onChange(updatedStyles);
    }

    setEditingStyle(null);
    setEditingIndex(null);
    setIsAdding(false);
  };

  const handleCancelEdit = () => {
    setEditingStyle(null);
    setEditingIndex(null);
    setIsAdding(false);
  };

  const handleDeleteStyle = (index: number) => {
    const updatedStyles = conversationStyles.filter((_, i) => i !== index);
    onChange(updatedStyles);
  };

  const handleToggleActive = (index: number) => {
    const updatedStyles = [...conversationStyles];
    updatedStyles[index] = {
      ...updatedStyles[index],
      isActive: !updatedStyles[index].isActive
    };
    onChange(updatedStyles);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Customized Agents</h3>
        <Button
          type="button"
          variant="primary"
          onClick={handleAddStyle}
          className="inline-flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Style
        </Button>
      </div>

      <div
        className={`rounded-lg border px-3 py-2 text-sm ${
          isVapiBotEngine(botEngine)
            ? 'border-violet-300/60 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100'
            : 'border-sky-300/60 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100'
        }`}
      >
        {voiceCatalogHint(botEngine, selfHostedTts)}
      </div>

      {botEngine === 'self_hosted' && (
        <div>
          <label htmlFor="self-hosted-tts-provider" className="block text-sm font-medium text-foreground mb-1">
            TTS provider
          </label>
          <select
            id="self-hosted-tts-provider"
            value={selfHostedTts}
            onChange={(e) =>
              handleTtsProviderChange(e.target.value === 'fish' ? 'fish' : 'elevenlabs')
            }
            className="select max-w-xs"
          >
            <option value="elevenlabs">ElevenLabs</option>
            <option value="fish">Fish Audio</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Changing provider clears voices that do not match — Fish and ElevenLabs IDs are different.
            Pick a new voice on each style, then save the campaign.
          </p>
          {conversationStyles.some(
            (style) => style.isActive && !voiceMatchesSelfHostedTts(style.selectedVoiceId, selfHostedTts)
          ) && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-400" role="alert">
              Active styles still have the other provider&apos;s voice, or no voice. Edit each one and
              choose a {selfHostedTts === 'fish' ? 'Fish Audio' : 'ElevenLabs'} voice or Update Campaign
              will be blocked.
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {conversationStyles.map((style, index) => (
          <React.Fragment key={index}>
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h4 className="font-medium text-foreground">{style.name}</h4>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      style.isActive
                        ? 'bg-success/10 text-success'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {style.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  <span className="mr-4">
                    <strong>Style:</strong> {styleLabels[style.conversationStyle]}
                  </span>
                  <span className="mr-4">
                    <strong>Personality:</strong> {personalityLabels[style.personalityType]}
                  </span>
                  <span className="mr-4">
                    <strong>Approach:</strong> {approachLabels[style.persuasionApproach]}
                  </span>
                  {isVapiBotEngine(botEngine) && (
                    <span className="mr-4">
                      <strong>Speech recognition:</strong> {style.transcriber || 'deepgram'}
                    </span>
                  )}
                  <span>
                    <strong>Voice:</strong> {styleVoiceLabel(style.selectedVoiceId, voices)}
                  </span>
                  {botEngine === 'self_hosted' &&
                    (style.selectedVoiceId || '').trim() &&
                    !voiceMatchesSelfHostedTts(style.selectedVoiceId, selfHostedTts) && (
                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                        This voice belongs to{' '}
                        {isFishVoiceId(style.selectedVoiceId) ? 'Fish Audio' : 'ElevenLabs'}. Switch
                        TTS provider back, or pick a matching voice.
                      </p>
                    )}
                  {botEngine === 'self_hosted' &&
                    style.isActive &&
                    !(style.selectedVoiceId || '').trim() && (
                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                        Pick a {selfHostedTts === 'fish' ? 'Fish Audio' : 'ElevenLabs'} voice before
                        saving the campaign.
                      </p>
                    )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleActive(index)}
                  className={`p-1 ${
                    style.isActive
                      ? 'text-success hover:text-success/80'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={style.isActive ? 'Deactivate' : 'Activate'}
                >
                  {style.isActive ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditStyle(index)}
                  className="p-1 text-muted-foreground hover:text-foreground"
                  title="Edit"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteStyle(index)}
                  className="p-1 text-destructive hover:text-destructive/80"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            </div>
            
            {/* Show edit form inline below this specific style */}
            {editingStyle && editingIndex === index && (
              <div className="ml-4">
                <ConversationStyleForm
                    style={editingStyle}
                    onStyleChange={(style) => setEditingStyle(style)}
                    onSave={() => handleSaveStyle()}
                    onCancel={handleCancelEdit}
                    onGeneratePrompt={(style) => handleGeneratePrompt(style)}
                    campaignId={campaignId}
                    generatingPrompt={generatingPrompt}
                    voices={voices}
                    loadingVoices={loadingVoices}
                    voiceError={voiceError}
                    isEditing={true}
                    voiceHint={voiceCatalogHint(botEngine, selfHostedTts)}
                    voiceLabel={voiceFieldLabel(botEngine, selfHostedTts)}
                    showTranscriber={isVapiBotEngine(botEngine)}
                    showTtsProviderToggle={botEngine === 'self_hosted'}
                    ttsProvider={selfHostedTts}
                    onTtsProviderChange={handleTtsProviderChange}
                  />
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Add new style form at the end */}
        {editingStyle && isAdding && (
          <ConversationStyleForm
              style={editingStyle}
              onStyleChange={(style) => setEditingStyle(style)}
              onSave={() => handleSaveStyle()}
              onCancel={handleCancelEdit}
              onGeneratePrompt={(style) => handleGeneratePrompt(style)}
              campaignId={campaignId}
              generatingPrompt={generatingPrompt}
              voices={voices}
              loadingVoices={loadingVoices}
              voiceError={voiceError}
              isEditing={false}
              voiceHint={voiceCatalogHint(botEngine, selfHostedTts)}
              voiceLabel={voiceFieldLabel(botEngine, selfHostedTts)}
              showTranscriber={isVapiBotEngine(botEngine)}
              showTtsProviderToggle={botEngine === 'self_hosted'}
              ttsProvider={selfHostedTts}
              onTtsProviderChange={handleTtsProviderChange}
            />
        )}

        {conversationStyles.length === 0 && !editingStyle && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No conversation styles added yet.</p>
            <p className="text-sm mt-1">Click "Add Style" to create your first conversation style.</p>
          </div>
        )}
      </div>
    </div>
  );
};